import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import SignatureViewer from '../components/SignatureViewer.jsx';
import RiskCard from '../components/RiskCard.jsx';
import { useTenantHref } from '../lib/tenantPath.jsx';

export default function PatientDetail() {
  const tHref = useTenantHref();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [prescribers, setPrescribers] = useState(null);
  const [prescribersLoading, setPrescribersLoading] = useState(false);

  async function load() { setData(await api.patient(id)); }
  async function loadPrescribers() {
    setPrescribersLoading(true);
    try { setPrescribers(await api.prescribers(id)); }
    catch (e) { setPrescribers({ error: e.message, prescribers: [] }); }
    finally { setPrescribersLoading(false); }
  }
  useEffect(() => {
    load().catch(console.error);
    loadPrescribers();
    const i = setInterval(loadPrescribers, 60_000);
    return () => clearInterval(i);
  }, [id]);

  if (!data) return <div className="text-slate-500">Loading...</div>;
  const { patient: p, prescriptions, events, alerts, deliveries } = data;

  async function toggleM2B() {
    const reason = prompt('Reason for override?');
    if (!reason) return;
    await api.override(p.id, { m2b_eligible: !p.m2b_eligible, reason });
    load();
  }

  async function updateRxStatus(rxId, status) {
    await api.updateRx(rxId, { status });
    load();
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{p.first_name} {p.last_name}</h1>
          <div className="text-sm text-slate-500 mt-1">
            MRN {p.mrn} · Room {p.room_number || '—'} · DOB {p.dob || '—'}
            {p.phone && <> · {p.phone}</>}
          </div>
          <div className="flex gap-2 mt-2">
            <span className="badge-neutral">{p.status}</span>
            {p.m2b_eligible ? <span className="badge-success">M2B Eligible</span> : <span className="badge-neutral">Not M2B</span>}
            {p.discharge_planned_at && <span className="badge-info">Discharge {new Date(p.discharge_planned_at).toLocaleString()}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={tHref(`/payment/${p.id}`)} className="btn-ghost">Take Payment</Link>
          <Link to={tHref(`/call/${p.id}`)} className="btn-ghost">Start Call</Link>
          <button onClick={toggleM2B} className="btn-primary">
            {p.m2b_eligible ? 'Remove M2B' : 'Mark M2B Eligible'}
          </button>
        </div>
      </div>

      <RiskCard patientId={p.id} />

      {alerts.length > 0 && (
        <div className="card p-4">
          <h2 className="font-semibold mb-2">Open Alerts</h2>
          <ul className="space-y-2">
            {alerts.map(a => (
              <li key={a.id} className="text-sm border-l-4 pl-3 py-1"
                  style={{ borderColor: a.severity === 'critical' ? '#dc2626' : a.severity === 'warning' ? '#d97706' : '#2563eb' }}>
                <div className="font-medium">{a.title}</div>
                <div className="text-slate-500">{a.message}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-4">
          <h2 className="font-semibold mb-3">Prescriptions</h2>
          <ul className="divide-y divide-slate-100">
            {prescriptions.map(rx => (
              <li key={rx.id} className="py-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{rx.drug_name} {rx.strength}</div>
                    <div className="text-xs text-slate-500">
                      {rx.rx_number || '—'} · NDC {rx.ndc || '—'} · Qty {rx.quantity || '—'}
                    </div>
                    {rx.directions && <div className="text-xs text-slate-600 mt-1 italic">{rx.directions}</div>}
                    <div className="flex gap-1 mt-1">
                      <span className="badge-neutral">{rx.status}</span>
                      {rx.high_risk && <span className="badge-warning">High-risk</span>}
                      {rx.counseling_required && !rx.counseling_completed_at && <span className="badge-info">Counsel needed</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {rx.status === 'new' && <button onClick={() => updateRxStatus(rx.id, 'received')} className="text-xs hover:underline" style={{ color: 'var(--brand-primary)' }}>Mark received</button>}
                    {rx.status === 'received' && <button onClick={() => updateRxStatus(rx.id, 'filled')} className="text-xs hover:underline" style={{ color: 'var(--brand-primary)' }}>Mark filled</button>}
                    {rx.status === 'filled' && <button onClick={() => updateRxStatus(rx.id, 'verified')} className="text-xs hover:underline" style={{ color: 'var(--brand-primary)' }}>Verify</button>}
                    {rx.status === 'verified' && <button onClick={() => updateRxStatus(rx.id, 'dispensed')} className="text-xs hover:underline" style={{ color: 'var(--brand-primary)' }}>Dispense</button>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="card p-4">
          <h2 className="font-semibold mb-3">Timeline</h2>
          <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3">
            {events.map(e => (
              <li key={e.id} className="ml-4 text-sm">
                <div className="absolute -left-1.5 w-3 h-3 rounded-full mt-1.5" style={{ background: 'var(--brand-primary)' }} />
                <div className="font-medium">{e.event_type}</div>
                <div className="text-xs text-slate-500">
                  {new Date(e.occurred_at).toLocaleString()} · <span className="font-mono">{e.source}</span>
                  {e.new_status && <> · → {e.new_status}</>}
                </div>
              </li>
            ))}
            {events.length === 0 && <li className="text-xs text-slate-400 ml-4">No events</li>}
          </ol>
        </div>
      </div>

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold flex items-center gap-2">
            Prescribers
            <span className="flex items-center gap-1 text-xs font-normal text-slate-500">
              <span className={`w-1.5 h-1.5 rounded-full ${prescribersLoading ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500'}`} />
              live FHIR · auto-refresh 60s
            </span>
          </h2>
          <button onClick={loadPrescribers} className="text-xs text-slate-500 hover:underline">
            {prescribersLoading ? 'Refreshing…' : '↻ Refresh now'}
          </button>
        </div>
        {prescribersLoading && !prescribers && <div className="text-sm text-slate-500">Querying EHR…</div>}
        {prescribers?.error && <div className="text-sm text-red-600">FHIR error: {prescribers.error}</div>}
        {prescribers?.prescribers?.length === 0 && <div className="text-sm text-slate-500">No prescribers on file.</div>}
        {prescribers?.prescribers?.length > 0 && (
          <div className="grid md:grid-cols-2 gap-3">
            {prescribers.prescribers.map(({ npi, source, practitioner: pr }) => (
              <div key={npi} className="border border-slate-200 rounded-md p-3 text-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">
                      {pr.name?.prefix} {pr.name?.given} {pr.name?.family} {pr.name?.suffix}
                    </div>
                    <div className="text-xs text-slate-500">
                      NPI <span className="font-mono">{npi}</span>
                      {source === 'unresolved' && <span className="ml-2 text-amber-700">· not in EHR directory</span>}
                    </div>
                  </div>
                  {pr.active === false && <span className="text-xs text-red-700">inactive</span>}
                </div>
                {pr.specialty?.length > 0 && (
                  <div className="text-xs text-slate-600 mt-1">
                    {pr.specialty.join(', ')}{pr.org ? <> · {pr.org}</> : null}
                  </div>
                )}
                {pr.telecom?.length > 0 && (
                  <ul className="text-xs text-slate-600 mt-2 space-y-0.5">
                    {pr.telecom.map((t, i) => (
                      <li key={i}><span className="text-slate-400 inline-block w-12">{t.system}:</span> {t.value}</li>
                    ))}
                  </ul>
                )}
                {pr.address && (
                  <div className="text-xs text-slate-500 mt-1">
                    {pr.address.line?.join(', ')}, {pr.address.city}, {pr.address.state} {pr.address.postalCode}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {prescribers?.fetched_at && (
          <div className="text-xs text-slate-400 mt-2">
            Fetched {new Date(prescribers.fetched_at).toLocaleTimeString()} from EHR FHIR
          </div>
        )}
      </div>

      {deliveries.length > 0 && (
        <div className="card p-4">
          <h2 className="font-semibold mb-3">Deliveries</h2>
          <ul className="divide-y divide-slate-100 text-sm">
            {deliveries.map(d => (
              <li key={d.id} className="py-2 flex items-center justify-between">
                <div>
                  <div className="font-medium">{d.delivery_location}</div>
                  <div className="text-xs text-slate-500">Scheduled {d.scheduled_at ? new Date(d.scheduled_at).toLocaleString() : '—'}
                    {d.delivered_at && <> · Delivered {new Date(d.delivered_at).toLocaleString()}</>}
                  </div>
                </div>
                <span className="badge-neutral">{d.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <PatientTransactions patientId={p.id} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// PatientTransactions - payments + linked deliveries + their prescriptions +
// signature proof of consent + per-payment "email receipt" action.
// -----------------------------------------------------------------------------
function PatientTransactions({ patientId }) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [from, setFrom] = useState('');
  const [to,   setTo]   = useState('');

  async function load() {
    try {
      const params = {};
      if (from) params.from = new Date(from).toISOString();
      if (to)   params.to   = new Date(to + 'T23:59:59').toISOString();
      const r = await api.patientTransactions(patientId, params);
      setData(r); setErr(null);
    } catch (e) { setErr(e.body?.message || e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const txns = data?.transactions || [];
  const patientEmail = data?.patient?.email || '';

  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <h2 className="font-semibold">Transactions &amp; deliveries</h2>
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label className="block">
            <span className="text-xs text-slate-600">From</span>
            <input type="date" className="input" value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs text-slate-600">To</span>
            <input type="date" className="input" value={to} onChange={e => setTo(e.target.value)} />
          </label>
          <button className="btn-ghost" onClick={load}>Apply</button>
        </div>
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">{err}</div>}

      {txns.length === 0 && (
        <div className="text-sm text-slate-500 italic py-4">No payment transactions for this patient in the selected window.</div>
      )}

      <ul className="space-y-3">
        {txns.map(t => (
          <TransactionRow key={t.id} t={t} fallbackEmail={patientEmail} onAfterSend={load} />
        ))}
      </ul>
    </div>
  );
}

function TransactionRow({ t, fallbackEmail, onAfterSend }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailTo, setEmailTo] = useState(t.email_to || fallbackEmail || '');
  const [includeDesc, setIncludeDesc] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  async function sendReceipt() {
    setBusy(true); setMsg(null); setErr(null);
    try {
      const r = await api.emailReceipt(t.id, { to: emailTo, include_description: includeDesc });
      const status = r.result?.status || 'unknown';
      setMsg(status === 'sent' ? 'Receipt sent.'
            : status === 'skipped' ? `Skipped: ${r.result?.reason || 'email provider not configured'}`
            : `Status: ${status}`);
      onAfterSend?.();
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }

  const ok = t.status === 'succeeded' || t.status === 'paid' || t.status === 'complete';
  return (
    <li className="border border-slate-200 rounded-lg overflow-hidden">
      <button type="button" className="w-full p-3 flex flex-wrap items-center gap-3 text-left hover:bg-slate-50"
        onClick={() => setOpen(o => !o)}>
        <span className={`px-2 py-0.5 rounded text-xs ${ok ? 'bg-emerald-100 text-emerald-800'
          : t.status === 'failed' || t.status === 'canceled' ? 'bg-red-100 text-red-800'
          : 'bg-slate-100 text-slate-700'}`}>{t.status}</span>
        <span className="text-sm">{new Date(t.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</span>
        <span className="text-sm font-mono ml-auto">{(t.currency || 'usd').toUpperCase()} ${Number(t.amount).toFixed(2)}</span>
        <span className="text-xs text-slate-500">{t.flow}</span>
        <span className="text-slate-400 text-sm">{open ? 'v' : '>'}</span>
      </button>

      {open && (
        <div className="p-4 border-t border-slate-100 space-y-4 bg-slate-50/50">
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            <Field label="Transaction ID" value={t.processor_payment_id || t.processor_checkout_id || t.id} mono />
            <Field label="Processor" value={`${t.processor || ''} - ${t.flow || ''}`} />
            <Field label="Description" value={t.description || '-'} />
            {t.email_status && <Field label="Email" value={`${t.email_status}${t.email_to ? ` - ${t.email_to}` : ''}`} />}
            {t.sms_status   && <Field label="SMS"   value={`${t.sms_status}${t.sms_to ? ` - ${t.sms_to}` : ''}`} />}
          </div>

          {t.delivery_id && (
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Delivery</div>
              <div className="text-sm">
                <span className="font-medium">{t.delivery_location || 'Unknown location'}</span>
                {t.delivery_status && <span className="ml-2 badge-neutral">{t.delivery_status}</span>}
                {t.delivery_delivered_at && <span className="ml-2 text-xs text-slate-500">delivered {new Date(t.delivery_delivered_at).toLocaleString()}</span>}
                {t.delivery_signed_by && <span className="ml-2 text-xs text-slate-500">signed by {t.delivery_signed_by}</span>}
              </div>
              {t.rx_list && t.rx_list.length > 0 && (
                <ul className="mt-2 text-sm divide-y divide-slate-100 bg-white border border-slate-200 rounded">
                  {t.rx_list.map(rx => (
                    <li key={rx.id} className="px-3 py-2 flex flex-wrap gap-x-3 gap-y-1">
                      <span className="font-mono text-xs text-slate-500">{rx.rx_number || '-'}</span>
                      <span className="font-medium">{rx.drug_name}{rx.strength ? ` ${rx.strength}` : ''}</span>
                      {rx.directions && <span className="text-xs text-slate-500 w-full sm:w-auto">{rx.directions}</span>}
                      {rx.copay_amount != null && <span className="text-xs text-slate-500">copay ${Number(rx.copay_amount).toFixed(2)}</span>}
                      <span className="ml-auto text-xs"><span className="badge-neutral">{rx.status}</span></span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div>
            <SignatureViewer payment={t} />
          </div>

          <div className="border-t border-slate-200 pt-3">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Email receipt</div>
            <div className="flex flex-wrap items-center gap-2">
              <input className="input flex-1 min-w-[220px]" placeholder="patient@example.com"
                value={emailTo} onChange={e => setEmailTo(e.target.value)} />
              <label className="text-xs flex items-center gap-1.5">
                <input type="checkbox" checked={includeDesc} onChange={e => setIncludeDesc(e.target.checked)} />
                Include description in email
              </label>
              <button className="btn-primary" disabled={busy || !emailTo} onClick={sendReceipt}>
                {busy ? 'Sending...' : (t.email_sent_at ? 'Resend receipt' : 'Email receipt')}
              </button>
            </div>
            {t.email_sent_at && (
              <div className="text-xs text-slate-500 mt-1">
                Last sent {new Date(t.email_sent_at).toLocaleString()} - {t.email_status}
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-1 leading-snug">
              Receipt includes facility name, patient first name, amount, last4, transaction ID, and a generic line item.
              No medication names or MRN are included unless you explicitly check "Include description".
            </div>
            {msg && <div className="text-xs text-emerald-700 mt-1">{msg}</div>}
            {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
          </div>
        </div>
      )}
    </li>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono break-all' : ''}`}>{value}</div>
    </div>
  );
}
